import { useState, useEffect, useRef } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Ship, MapPin, Wrench, CheckCircle2, Clock, Users, Gauge, Anchor, ChevronRight, BookOpen, X, Map as MapIcon, Database } from "lucide-react";
import { MOCK_VESSELS, VESSEL_CATALOGUE, CLIENT_DISPLAY_NAMES, OFFSHORE_ASSETS, VESSEL_STATUS_COLORS } from "@/data/portalMockData";
import { useVessels, useClients } from "@/hooks/useFleetData";
import vesselRender1 from "@/assets/vessel-render-1.jpg";
import vesselRender2 from "@/assets/vessel-render-2.jpg";
import vesselRender3 from "@/assets/vessel-render-3.jpg";
import aircatSonangol from "@/assets/aircat-sonangol.jpg";

const vesselImages = [vesselRender1, vesselRender2, vesselRender3];

const CatalogueCard = ({ v }: { v: typeof VESSEL_CATALOGUE[0] }) => {
  const [showLightbox, setShowLightbox] = useState(false);
  return (
    <Card className="overflow-hidden">
      <div className="w-full overflow-hidden cursor-pointer group relative" onClick={() => setShowLightbox(true)}>
        <img src={v.image} alt={v.name} className="w-full h-[260px] object-cover transition-transform duration-500 group-hover:scale-105" loading="eager" />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold bg-black/50 px-3 py-1.5 rounded-full backdrop-blur-sm">Click to enlarge</span>
        </div>
      </div>
      {showLightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setShowLightbox(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setShowLightbox(false)}>
            <X className="w-8 h-8" />
          </button>
          <img src={v.image} alt={v.name} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="text-white font-bold text-lg">{v.name}</p>
            <p className="text-white/60 text-xs uppercase tracking-[2px]">{v.type}</p>
          </div>
        </div>
      )}
      <CardContent className="p-5 space-y-3">
        <div>
          <h3 className="font-display text-lg text-foreground">{v.name}</h3>
          <p className="text-[11px] font-semibold uppercase tracking-[2px] text-teal mt-0.5">{v.type}</p>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed">{v.description}</p>
        <div className="grid grid-cols-2 gap-2">
          {v.specs.map(spec => (
            <div key={spec.label} className="p-2 rounded bg-muted/50">
              <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{spec.label}</p>
              <p className="text-xs font-medium text-foreground">{spec.value}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ── Fleet Map ──────────────────────────────────────────────────
const FleetMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    let map: any;
    const loadMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (!mapRef.current) return;

      map = L.map(mapRef.current, {
        center: [0, -20], zoom: 2,
        zoomControl: false, attributionControl: false,
      });
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19 }).addTo(map);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", { maxZoom: 19 }).addTo(map);
      L.control.zoom({ position: "bottomright" }).addTo(map);

      MOCK_VESSELS.forEach((v) => {
        const statusInfo = VESSEL_STATUS_COLORS[v.status] || VESSEL_STATUS_COLORS.available;
        const color = statusInfo.bg;
        const isMoving = v.status === "in_transit";
        const pulseRing = isMoving ? `<div style="position:absolute;top:-4px;left:-4px;width:20px;height:20px;border-radius:50%;border:2px solid ${color};opacity:0.4;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>` : "";
        const icon = L.divIcon({
          className: "",
          html: `<div style="position:relative;width:14px;height:14px;">${pulseRing}<div style="position:absolute;top:1px;left:1px;width:14px;height:14px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div></div>`,
          iconSize: [14, 14], iconAnchor: [7, 7],
        });
        const clientDisplay = CLIENT_DISPLAY_NAMES[v.client] || v.client;
        const alongsideInfo = v.status === "alongside" ? `<div style="font-size:11px;color:#28A745;font-weight:600;margin:4px 0;">⚓ Alongside ${v.destination || ""}</div>` : "";
        const transitInfo = v.status === "in_transit" ? `<div style="font-size:11px;color:#0066CC;font-weight:600;margin:4px 0;">🚢 En route to ${v.destination || ""}</div>` : "";
        const shoreInfo = v.status === "available" ? `<div style="font-size:11px;color:#17A2B8;font-weight:600;margin:4px 0;">🏠 At Shore Base</div>` : "";
        const paxLine = v.paxOnBoard > 0
          ? `<div style="font-size:11px;margin:2px 0;">👥 <b>${v.paxOnBoard}</b> passengers on board</div>`
          : `<div style="font-size:11px;color:#888;margin:2px 0;">No passengers on board</div>`;
        L.marker([v.lat, v.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:240px;max-width:300px;">
              <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${v.name}</div>
              <div style="font-size:11px;color:#666;margin-bottom:6px;">${v.type}</div>
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};"></span>
                <span style="font-size:12px;font-weight:600;">${statusInfo.label}</span>
              </div>
              ${alongsideInfo}${transitInfo}${shoreInfo}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:11px;margin:2px 0;">📋 <b>Client:</b> ${clientDisplay}</div>
              <div style="font-size:11px;margin:2px 0;">🏴 <b>Flag:</b> ${v.flag}</div>
              <div style="font-size:11px;margin:2px 0;">⚡ <b>Speed:</b> ${v.currentSpeed > 0 ? v.currentSpeed + " kts" : "0 kts (stationary)"}</div>
              <div style="font-size:11px;margin:2px 0;">👨‍✈️ <b>Captain:</b> ${v.captain}</div>
              <div style="font-size:11px;margin:2px 0;">🔧 <b>Chief Engineer:</b> ${v.chiefEngineer}</div>
              ${paxLine}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:10px;color:#aaa;">IMO: ${v.imoNumber} · MMSI: ${v.mmsi}</div>
            </div>
          `, { maxWidth: 320 });
      });

      const assetColors: Record<string, string> = { FPSO: "#E74C3C", Drillship: "#3498DB", Terminal: "#FF9F1C", "Semi-Sub": "#F39C12" };
      OFFSHORE_ASSETS.forEach((asset) => {
        const color = assetColors[asset.type] || "#fff";
        const opStatus = asset.operatorStatus || "operated";
        const size = asset.type === "Terminal" ? 18 : 14;
        let shape: string;
        if (asset.type === "Terminal") {
          const opacity = opStatus === "future" ? "0.4" : opStatus === "non-operated" ? "0.6" : "1";
          shape = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" style="filter:drop-shadow(0 0 4px ${color}80);opacity:${opacity};"><path d="M12 2L12 10M12 10C9.79 10 8 11.79 8 14M12 10C14.21 10 16 11.79 16 14M8 14H5L7 22H17L19 14H16M8 14H16M6 6H18" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        } else if (opStatus === "future") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:transparent;border:2px dashed ${color};box-shadow:0 0 10px ${color}40;${tr}"></div>`;
        } else if (opStatus === "non-operated") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:${color}55;border:2px dashed #fff;box-shadow:0 0 10px ${color}60;${tr}"></div>`;
        } else {
          shape = asset.type === "FPSO"
            ? `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;box-shadow:0 0 10px ${color}80;transform:rotate(45deg);"></div>`
            : `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 10px ${color}80;"></div>`;
        }
        const icon = L.divIcon({ className: "", html: shape, iconSize: [size, size], iconAnchor: [size / 2, size / 2] });
        const clientDisplay = CLIENT_DISPLAY_NAMES[asset.client] || asset.client;
        const statusLabels: Record<string, string> = { operated: "", "non-operated": "🟡 Non-Operated Interest", future: "🔵 Under Construction" };
        const statusLine = opStatus !== "operated" ? `<div style="font-size:11px;font-weight:600;margin:4px 0;">${statusLabels[opStatus]}</div>` : "";
        L.marker([asset.lat, asset.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:200px;">
              <div style="font-weight:700;font-size:14px;">${asset.name}</div>
              <div style="font-size:11px;color:#888;">${asset.type} · ${asset.region}</div>
              <div style="font-size:11px;color:#888;">Client: ${clientDisplay}</div>
              ${statusLine}
              ${asset.field ? `<div style="font-size:11px;color:#aaa;">Field: ${asset.field}</div>` : ""}
            </div>
          `);
      });

      const allPoints = [
        ...MOCK_VESSELS.map((v) => [v.lat, v.lng] as [number, number]),
        ...OFFSHORE_ASSETS.map((a) => [a.lat, a.lng] as [number, number]),
      ];
      const bounds = L.latLngBounds(allPoints);
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 6 });
      setMapReady(true);
    };
    loadMap();
    return () => { map?.remove(); };
  }, []);

  return (
    <div className="relative rounded-xl overflow-hidden border border-border" style={{ height: 500 }}>
      {!mapReady && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-muted">
          <div className="w-6 h-6 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      {/* Legend */}
      <div className="absolute bottom-3 left-3 z-[1000] bg-background/90 backdrop-blur-sm rounded-lg p-3 border border-border text-xs space-y-1.5">
        <p className="font-semibold text-foreground mb-1">Legend</p>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> On-Hire / Alongside</div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> In Transit</div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> Standby</div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#E74C3C" }}></span> FPSO</div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#3498DB" }}></span> Drillship</div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#2ECC71" }}></span> Terminal</div>
        <div className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ background: "#F39C12" }}></span> Semi-Sub</div>
      </div>
    </div>
  );
};

const statusConfig: Record<string, { label: string; color: string }> = {
  on_charter: { label: "ON-HIRE", color: "bg-emerald-500 text-white" },
  available: { label: "ON-HIRE", color: "bg-emerald-500 text-white" },
  standby: { label: "STANDBY", color: "bg-amber-500 text-foreground" },
  in_transit: { label: "IN TRANSIT", color: "bg-blue-500 text-white" },
  alongside: { label: "ON-HIRE", color: "bg-emerald-500 text-white" },
  maintenance: { label: "MAINTENANCE", color: "bg-orange-500 text-white" },
  off_hire: { label: "OFF-HIRE", color: "bg-red-500 text-white" },
};

const EmployeeFleet = () => {
  const { data: liveVessels = [] } = useVessels();
  const { data: liveClients = [] } = useClients();

  // Merge live (admin-entered) vessels with mock demo vessels — live show first
  const liveAdapted = liveVessels
    .filter(v => !v.is_demo)
    .map((v, i) => {
      const client = liveClients.find(c => c.id === v.assigned_client_id);
      return {
        id: v.id,
        name: v.name,
        type: v.vessel_type,
        status: v.status,
        client: client?.name || "Unassigned",
        clientDisplay: client?.name || "Unassigned",
        flag: v.flag || "—",
        captain: "—",
        chiefEngineer: "—",
        region: client?.region || "—",
        homePort: v.current_position || "—",
        destination: v.current_position || "",
        paxCapacity: 0,
        paxOnBoard: 0,
        currentSpeed: 0,
        maxSpeed: 0,
        imoNumber: v.imo_number || "—",
        mmsi: "—",
        lat: 0,
        lng: 0,
        image: vesselImages[i % vesselImages.length],
        isLive: true,
      };
    });

  const mockAdapted = MOCK_VESSELS.map((v, i) => ({
    ...v,
    image: (v.client === "Client B" || v.client === "Client C" || v.client === "Client D") ? aircatSonangol : vesselImages[i % vesselImages.length],
    clientDisplay: CLIENT_DISPLAY_NAMES[v.client] || v.client,
    isLive: false,
  }));

  const allVessels = [...liveAdapted, ...mockAdapted];
  const onHireCount = allVessels.filter(v => ["on_charter", "available", "in_transit", "alongside"].includes(v.status)).length;

  return (
    <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-foreground">Fleet & Vessels</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            All vessels, contract assignments, and catalogue
            {liveAdapted.length > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 text-teal">
                <Database className="w-3 h-3" /> {liveAdapted.length} live record{liveAdapted.length === 1 ? "" : "s"}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Vessels On-Hire", value: `${onHireCount}/${allVessels.length}`, icon: Ship, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Fleet Utilization", value: "94.2%", icon: Gauge, color: "text-teal", bg: "bg-teal/10" },
          { label: "On-Time Performance", value: "98.1%", icon: Anchor, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Total PAX Capacity", value: allVessels.reduce((s, v) => s + v.paxCapacity, 0).toString(), icon: Users, color: "text-cyan-600", bg: "bg-cyan-50" },
        ].map(kpi => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.label}>
              <CardContent className="p-4">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${kpi.bg}`}>
                  <Icon className={`w-4 h-4 ${kpi.color}`} />
                </div>
                <p className="text-2xl font-bold text-foreground">{kpi.value}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{kpi.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Tabs defaultValue="fleet" className="space-y-4">
        <TabsList>
          <TabsTrigger value="fleet">All Vessels</TabsTrigger>
          <TabsTrigger value="map">Map View</TabsTrigger>
          <TabsTrigger value="catalogue">Vessel Catalogue</TabsTrigger>
        </TabsList>

        <TabsContent value="fleet">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {allVessels.map(vessel => {
              const st = statusConfig[vessel.status] || statusConfig.available;
              return (
                <Card key={vessel.id} className="overflow-hidden hover:shadow-md transition-shadow">
                  <div className="relative h-40 overflow-hidden">
                    <img src={vessel.image} alt={vessel.name} className="w-full h-full object-cover" />
                    <div className="absolute top-3 right-3 flex flex-col gap-1.5 items-end">
                      <span className={`px-2.5 py-1 rounded text-[10px] font-bold tracking-[1px] uppercase ${st.color}`}>{st.label}</span>
                      {vessel.isLive && (
                        <Badge className="bg-teal text-white text-[9px] gap-1"><Database className="w-2.5 h-2.5" /> LIVE</Badge>
                      )}
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent pt-10 pb-3 px-4">
                      <h3 className="font-bold text-base text-white">{vessel.name}</h3>
                      <p className="text-[11px] text-white/60">{vessel.type}</p>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    {/* Contract assignment */}
                    <div className="flex items-center gap-2 mb-3 px-2.5 py-1.5 rounded-md bg-teal/5 border border-teal/15">
                      <Anchor className="w-3 h-3 text-teal flex-shrink-0" />
                      <span className="text-xs font-medium text-foreground truncate">Contracted to: {vessel.clientDisplay}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                      <MapPin className="w-3 h-3 text-teal/60" />
                      <span className="truncate">{vessel.destination || vessel.homePort || vessel.region}</span>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      {[
                        { label: "Region", value: vessel.region },
                        { label: "PAX Cap", value: vessel.paxCapacity.toString() },
                        { label: "Speed", value: `${vessel.maxSpeed} kts` },
                        { label: "Captain", value: vessel.captain.replace("Capt. ", "") },
                      ].map(stat => (
                        <div key={stat.label}>
                          <p className="text-xs font-medium text-foreground">{stat.value}</p>
                          <p className="text-[9px] text-muted-foreground mt-0.5">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>


        <TabsContent value="map">
          <FleetMap />
        </TabsContent>

        <TabsContent value="catalogue">
          <p className="text-sm text-muted-foreground mb-4">
            Vessels available for charter or newbuild — not currently contracted.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VESSEL_CATALOGUE.map(v => (
              <CatalogueCard key={v.id} v={v} />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </>
  );
};

export const EmployeeFleetContent = EmployeeFleet;

const EmployeeFleetPage = () => (
  <IntranetLayout>
    <EmployeeFleetContent />
  </IntranetLayout>
);

export default EmployeeFleetPage;
